import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ────────────────────────────────────────────────────────────────── */
    :host {
      display: block;
      height: 100%;
      box-sizing: border-box;

      /* Width — animates between expanded and collapsed. */
      width: var(--uwc-sidebar-width, 260px);
      transition: width var(--uwc-sidebar-duration, 220ms) cubic-bezier(0.16, 1, 0.3, 1);

      /*
       * Root cascade tokens. The sidebar inherits the panelmenu palette by
       * default so it visually matches uwc-panelmenu out of the box.
       */
      --_bg:    var(--uwc-sidebar-bg,    var(--uwc-panelmenu-bg,    var(--uwc-surface, ${t.surface})));
      --_color: var(--uwc-sidebar-color, var(--uwc-panelmenu-color, var(--uwc-text,    ${t.text})));
    }

    :host([collapsed]) {
      width: var(--uwc-sidebar-collapsed-width, 72px);
    }

    @media (prefers-reduced-motion: reduce) {
      :host { transition: none; }
    }

    /* ── Shell ───────────────────────────────────────────────────────────────── */
    .sb {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      overflow: hidden;
      background: var(--_bg);
      color:      var(--_color);
      border-inline-end: var(--uwc-sidebar-border, 1px solid ${t.border});
      font-size: var(--uwc-sidebar-font-size, ${t.fontSizeMd});
      font-family: var(--uwc-font-family, inherit);
    }

    /* ── Regions ─────────────────────────────────────────────────────────────── */
    .sb__start {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      min-height: var(--uwc-sidebar-start-height, 60px);
      padding: var(--uwc-sidebar-start-padding, ${t.space3} ${t.space4});
      border-bottom: 1px solid color-mix(in oklab, var(--_color) 10%, transparent);
      overflow: hidden;
    }

    .sb__nav {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      scrollbar-width: thin;
      scrollbar-color: color-mix(in oklab, var(--_color) 20%, transparent) transparent;
    }
    .sb__nav::-webkit-scrollbar { width: 5px; }
    .sb__nav::-webkit-scrollbar-track { background: transparent; }
    .sb__nav::-webkit-scrollbar-thumb {
      background: color-mix(in oklab, var(--_color) 20%, transparent);
      border-radius: ${t.radiusFull};
    }

    .sb__end {
      flex-shrink: 0;
      padding: var(--uwc-sidebar-end-padding, ${t.space3} ${t.space4});
      border-top: 1px solid color-mix(in oklab, var(--_color) 10%, transparent);
      overflow: hidden;
    }

    /* Centre the brand / footer content when collapsed. */
    .sb--collapsed .sb__start,
    .sb--collapsed .sb__end {
      justify-content: center;
      padding-inline: ${t.space2};
    }

    /* ── Expanded mode: blend uwc-panelmenu into the shell ───────────────────── */
    .sb__panelmenu {
      display: block;
      --uwc-panelmenu-bg: transparent;
      /*
       * Track the sidebar colour WITHOUT referencing the local --_color.
       * uwc-panelmenu's own :host sets --_color from --uwc-panelmenu-color on
       * this same element, so pointing --uwc-panelmenu-color back at --_color
       * forms a cycle that invalidates --_color and silently kills every
       * panelmenu hover/separator color-mix. Resolve from the source vars.
       */
      --uwc-panelmenu-color: var(--uwc-sidebar-color, var(--uwc-text, ${t.text}));
      --uwc-panelmenu-border: none;
      --uwc-panelmenu-radius: 0;
      --uwc-panelmenu-shadow: none;
    }

    /* ── Collapsed mode: icon rail ───────────────────────────────────────────── */
    .rail {
      list-style: none;
      margin: 0;
      padding: ${t.space2} 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: ${t.space1};
    }
    .rail__item {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    /* uwc-menu / uwc-tooltip hosts use display:contents, so the button is the box. */
    .rail__flyout { display: contents; }

    .rail__btn {
      --_size: var(--uwc-sidebar-rail-item-size, 44px);
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width:  var(--_size);
      height: var(--_size);
      padding: 0;
      border: none;
      border-radius: var(--uwc-sidebar-rail-item-radius, ${t.radiusMd});
      background: transparent;
      color: inherit;
      cursor: pointer;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color      ${t.durationFast} ${t.easingStandard};
    }
    .rail__btn:hover {
      background: var(--uwc-sidebar-item-hover-bg,
        color-mix(in oklab, var(--_color) 8%, transparent));
    }
    .rail__btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }
    .rail__btn--active {
      background: var(--uwc-sidebar-item-active-bg,
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 12%, transparent));
      color: var(--uwc-sidebar-item-active-color, var(--uwc-color-primary, ${t.primary}));
    }
    .rail__btn--danger { color: var(--uwc-color-danger, ${t.danger}); }
    .rail__btn--danger:hover {
      background: color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 12%, transparent);
    }
    .rail__btn--disabled {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Initial-letter fallback when an item has no icon. */
    .rail__initial {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: ${t.radiusFull};
      background: color-mix(in oklab, var(--_color) 12%, transparent);
      font-size: ${t.fontSizeSm};
      font-weight: ${t.fontWeightSemibold};
      line-height: 1;
    }

    /* Badge presence indicator (collapsed). */
    .rail__dot {
      position: absolute;
      top: 8px;
      inset-inline-end: 8px;
      width: 7px;
      height: 7px;
      border-radius: ${t.radiusFull};
      background: var(--uwc-sidebar-badge-color, var(--uwc-color-primary, ${t.primary}));
      box-shadow: 0 0 0 2px var(--_bg);
    }

    /* Flyout header (rendered into uwc-menu's start slot). */
    .rail__flyout-head {
      padding: ${t.space2} 13px;
      margin-bottom: ${t.space1};
      font-size: ${t.fontSizeSm};
      font-weight: ${t.fontWeightSemibold};
      color: var(--_color);
      border-bottom: 1px solid ${t.borderSubtle};
    }

    /* ── Mobile (drawer) mode ────────────────────────────────────────────────── */
    /*
     * In mobile mode the host reserves no layout space — the menu lives in a
     * uwc-drawer overlay (top layer) that is hidden until toggled. display:
     * contents keeps the host cascade tokens (--_bg/--_color) inheriting into
     * the drawer while removing its box from the surrounding flex/grid layout.
     */
    :host([mobile]) {
      display: contents;
      width: auto;
    }
    .sb__drawer { display: contents; }

    /* The accordion fills the drawer; the drawer body owns the scroll. */
    .sb__nav--drawer {
      overflow: visible;
      flex: initial;
    }
    /* Brand/footer flow into the drawer's own header/footer bars — drop the
       sidebar's own region chrome so it isn't doubled. */
    .sb__start--drawer {
      min-height: auto;
      padding: 0;
      border: 0;
    }
    .sb__end--drawer {
      padding: 0;
      border: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .sb__drawer,
      .rail__btn { transition: none; }
    }
  `,
];
